/**
 * DISTRIBUTION-MAP MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 *
 * Three map kinds (maps.map_type):
 *  - 'distributor' (default) & 'region': LIVE from System Settings — distributors
 *    or regions and which states each covers come from the shared `sales_states`
 *    data (referenceDataService). The editor only sets per-item color; no manual
 *    painting. Editing System Settings updates the map automatically.
 *  - 'other': MANUAL — the owner adds legend items, picks colors, and clicks
 *    states to paint them. Items live in `map_distributors`, assignments in
 *    `map_regions` (the original distribution-map model).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  loadMap,
  updateMapSettings,
  upsertDistributorByName,
  createDistributor,
  updateDistributor,
  deleteDistributor,
  setRegionDistributor,
  publishMap,
} from "../utils/mapsService.js";
import USMap from "../components/USMap.jsx";
import DistributorLegend from "../components/DistributorLegend.jsx";
import ExportModal from "../components/ExportModal.jsx";
import { useConfirm } from "../../../shared/components/ConfirmDialog.jsx";
import { useTheme } from "../../../shared/components/ThemeProvider.jsx";
import { US_STATE_NAME, nameToStateCode, defaultColorFor } from "../utils/usStateCodes.js";
import { resolveFills, countyGeometry, zoomViewBox, VIEWBOX } from "../../../../../map-core/render.js";
import { CALLOUT } from "../../../../../map-core/config.js";
import {
  listSalesStates,
  listSalesStateModes,
  listSalesCounties,
  listSalesCountiesForState,
} from "../../../shared/utils/referenceDataService.js";
import { loadCountyBundle, isoToStateFips, stateMeta } from "../../../shared/utils/countyGeo.js";
import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";
import { supabase } from "../../../shared/utils/supabaseClient.js";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
// Fraction of the map height where the VT callout box starts — the right-side
// legend lines its top up here (same reference the export uses).
const CALLOUT_TOP_FRAC = CALLOUT.top / (Number(VIEWBOX.split(/\s+/)[3]) || 600);

export default function MapEditor() {
  const { mapId } = useParams();
  const askConfirm = useConfirm();
  const { theme } = useTheme();
  const [map, setMap] = useState(null);
  const [mapDistributors, setMapDistributors] = useState([]); // legend items / per-map color overrides
  const [mapRegions, setMapRegions] = useState([]);            // 'other' map: manual state assignments
  const [salesStates, setSalesStates] = useState([]);          // System Settings source of truth (live maps)
  const [selectedDist, setSelectedDist] = useState(null);      // 'other' map: legend item armed for painting
  const [focusId, setFocusId] = useState(null);                // 'other' map: newly-added item to focus
  const [loaded, setLoaded] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [legendTop, setLegendTop] = useState(0);
  const [companyLogoUrl, setCompanyLogoUrl] = useState(null); // account logo for exports
  const [countyModeCodes, setCountyModeCodes] = useState(new Set()); // ISO states in by-county mode
  const [countyDistNames, setCountyDistNames] = useState([]);        // distinct county distributor names
  const [drill, setDrill] = useState(null);  // { iso, name, geo, fills, viewBox } when drilled in
  const [drillBusy, setDrillBusy] = useState(false);
  const [hoverCounty, setHoverCounty] = useState(null); // { name, distributor } in drill view
  const stageRef = useRef(null);
  const mapFrameRef = useRef(null);

  async function refresh() {
    try {
      const [r, states, modes, counties] = await Promise.all([
        loadMap(mapId),
        listSalesStates(),
        listSalesStateModes().catch(() => []),
        listSalesCounties().catch(() => []),
      ]);
      setMap(r.map);
      setMapDistributors(r.distributors);
      setMapRegions(r.regions);
      setSalesStates(states);

      // A state counts as by-county ONLY if it actually has active county rows.
      // The mode flag alone is not enough: a by-county state is excluded from the
      // state-level fills and legend (it hatches instead), so a state flagged
      // 'county' with nothing painted would lose its distributor here AND on the
      // published embed, and drilling into it would show an empty county map.
      // map-public applies the identical rule — the editor and the embed have to
      // agree about this or the customer sees one map and their visitors another.
      const activeCounties = counties.filter((c) => c.active !== false);
      const paintedFips = new Set(
        activeCounties.map((c) => String(c.county_fips).slice(0, 2))
      );
      const modeCodes = new Set();
      for (const m of modes.filter((m) => m.mode === "county")) {
        const fips = await isoToStateFips(m.state_code).catch(() => null);
        if (fips && paintedFips.has(fips)) modeCodes.add(m.state_code);
      }
      setCountyModeCodes(modeCodes);
      setCountyDistNames(
        [...new Set(activeCounties.map((c) => (c.distributor || "").trim()).filter(Boolean))]
      );
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoaded(true);
    }
  }
  useEffect(() => { refresh(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [mapId]);

  // Company (account) logo — branding for exports. Non-blocking; failure = no logo.
  useEffect(() => {
    let alive = true;
    getMyPrimaryAccount()
      .then((a) => { if (alive) setCompanyLogoUrl(a?.logoUrl || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Three modes: 'other' (manual painting) vs live 'distributor'/'region'.
  const isManual = map?.map_type === "other";
  const isRegion = map?.map_type === "region";
  const isDistributor = !!map && !isManual && !isRegion; // county feature: distributor maps only
  const groupLabel = isManual ? "Legend" : isRegion ? "Regions" : "Distributors";
  const groupNoun = isRegion ? "regions" : "distributors";

  const { distRows, regions, unmatched, stateCounts } = useMemo(() => {
    // 'other' map: legend items (map_distributors) + assignments (map_regions)
    // are owned per-map and painted by hand.
    if (isManual) {
      const stateCounts = {};
      for (const r of mapRegions) {
        if (r.level === "state" && r.active !== false && r.distributor) {
          stateCounts[r.distributor] = (stateCounts[r.distributor] || 0) + 1;
        }
      }
      return { distRows: mapDistributors, regions: mapRegions, unmatched: [], stateCounts };
    }
    // Live (distributor/region): derive the legend + assignments from System Settings.
    const groupOf = (s) => (isRegion ? s.region : s.distributor);
    const overrideByName = new Map(mapDistributors.map((d) => [d.name, d]));

    // Legend names: whole-state distributors (excluding by-county states, which
    // render from counties) + county distributors. Region maps ignore counties.
    const nameSet = new Set();
    for (const s of salesStates) {
      const g = groupOf(s);
      if (!s.active || !g || !String(g).trim()) continue;
      const code = nameToStateCode(s.state_name);
      if (isDistributor && code && countyModeCodes.has(code)) continue;
      nameSet.add(String(g).trim());
    }
    if (isDistributor) for (const n of countyDistNames) nameSet.add(n);
    const names = [...nameSet].sort((a, b) => a.localeCompare(b));

    const distRows = names.map((name, i) => {
      const ov = overrideByName.get(name);
      return {
        name,
        public_name: ov?.public_name || name,
        color_hex: HEX_RE.test(ov?.color_hex || "") ? ov.color_hex : defaultColorFor(i),
        show_on_map: ov ? ov.show_on_map !== false : true,
        sort_order: i,
      };
    });

    const regions = [];
    const unmatched = [];
    const stateCounts = {};
    for (const s of salesStates) {
      const g = groupOf(s);
      if (!s.active || !g || !String(g).trim()) continue;
      const code = nameToStateCode(s.state_name);
      const key = String(g).trim();
      if (!code) { unmatched.push(s.state_name); continue; }
      // By-county states render as the hatch (not a solid state color) and drill
      // into their counties — skip their whole-state assignment.
      if (isDistributor && countyModeCodes.has(code)) continue;
      // `distributor` here is the grouping key (a region name in region mode) —
      // resolveFills joins region_code -> this key -> color.
      regions.push({ level: "state", region_code: code, distributor: key, active: true });
      stateCounts[key] = (stateCounts[key] || 0) + 1;
    }
    return { distRows, regions, unmatched, stateCounts };
  }, [mapDistributors, mapRegions, salesStates, isManual, isRegion, isDistributor, countyModeCodes, countyDistNames]);

  const fills = useMemo(() => {
    // Only show_on_map distributors color the map (hidden ones -> unassigned),
    // matching the published embed.
    const distByName = new Map(
      distRows.filter((d) => d.show_on_map).map((d) => [d.name, { color_hex: d.color_hex }])
    );
    return resolveFills(regions, distByName);
  }, [distRows, regions]);

  // By-county states render as the neutral hatch nationally (distributor maps).
  const mixedCodes = useMemo(
    () => (isDistributor && countyModeCodes.size ? countyModeCodes : null),
    [isDistributor, countyModeCodes]
  );

  // Drill into a by-county state: load its counties (read-only here — county
  // assignment lives in System Settings) and shade them by distributor color.
  async function enterDrill(iso) {
    if (!isDistributor || !countyModeCodes.has(iso)) return;
    setDrillBusy(true);
    setErr(null);
    try {
      const fipsCode = await isoToStateFips(iso);
      if (!fipsCode) throw new Error("No county data for this state.");
      const [meta, bundle, rows] = await Promise.all([
        stateMeta(fipsCode),
        loadCountyBundle(fipsCode),
        listSalesCountiesForState(fipsCode),
      ]);
      const geo = countyGeometry(bundle);
      const distByName = new Map(
        distRows.filter((d) => d.show_on_map).map((d) => [d.name, { color_hex: d.color_hex }])
      );
      const countyRegions = rows
        .filter((r) => r.distributor)
        .map((r) => ({ level: "county", region_code: r.county_fips, distributor: r.distributor, active: true }));
      setDrill({
        iso,
        name: US_STATE_NAME[iso] || meta?.name || iso,
        geo,
        fills: resolveFills(countyRegions, distByName, "county"),
        dist: new Map(countyRegions.map((r) => [r.region_code, r.distributor])),
        viewBox: zoomViewBox(meta?.bbox || []),
      });
      setHoverCounty(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setDrillBusy(false);
    }
  }
  function exitDrill() { setDrill(null); setHoverCounty(null); }

  // National-view click: paint ('other'), or drill into a by-county state.
  function handleNationalClick(code) {
    if (isManual) { handleStateClick(code); return; }
    if (isDistributor && countyModeCodes.has(code)) enterDrill(code);
  }
  function handleDrillHover(fips) {
    if (!drill || !fips) { setHoverCounty(null); return; }
    setHoverCounty({ name: drill.geo.names?.[fips] || fips, distributor: drill.dist.get(fips) || null });
  }

  const legendRight = map?.map_legend_position === "right";

  // Legend text auto-contrasts unless the owner picked an explicit color. In the
  // editor the legend sits on the THEME surface (a sibling of the map), not on
  // the map's own background — so contrast against the theme, not map_bg_color.
  // (Export/embed contrast against the map background, where the legend really
  // does sit on it.)
  const legendInk = HEX_RE.test(map?.map_legend_text_color || "")
    ? map.map_legend_text_color
    : (theme === "dark" ? "#ffffff" : "#1f2937");

  // Keep the right-side legend's top lined up with the VT callout box as the map
  // resizes (the export aligns to the same line).
  useEffect(() => {
    const el = mapFrameRef.current;
    if (!legendRight || !el || typeof ResizeObserver === "undefined") {
      setLegendTop(0);
      return undefined;
    }
    const ro = new ResizeObserver(() => {
      setLegendTop(Math.round(el.clientHeight * CALLOUT_TOP_FRAC));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [legendRight]);

  const hoveredKey = hovered ? regions.find((r) => r.region_code === hovered)?.distributor || null : null;
  const hoveredDist = hoveredKey ? (distRows.find((d) => d.name === hoveredKey)?.public_name || hoveredKey) : null;

  // Persist a presentation change for a distributor (color / name / visibility).
  // We send the full current row so the upsert is consistent on insert + update.
  async function saveDist(row, patch) {
    try {
      await upsertDistributorByName(mapId, row.name, {
        color_hex: row.color_hex,
        public_name: row.public_name,
        show_on_map: row.show_on_map,
        sort_order: row.sort_order,
        ...patch,
      });
      await refresh();
    } catch (e) { setErr(e.message); }
  }

  // --- 'other' map only: manual paint + legend-item CRUD ---
  async function handleStateClick(stateCode) {
    if (!isManual) return;
    try {
      if (!selectedDist) {
        // Nothing armed: clicking an already-assigned state clears it.
        const prev = mapRegions.find((r) => r.level === "state" && r.region_code === stateCode);
        if (!prev) return;
        await setRegionDistributor(mapId, stateCode, null);
      } else {
        await setRegionDistributor(mapId, stateCode, selectedDist);
      }
      await refresh();
    } catch (e) { setErr(e.message); }
  }

  async function handleAddLegendItem() {
    try {
      // Opaque, stable key (the paint value + join key); the label is public_name.
      const name = `li-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
      const id = await createDistributor(mapId, {
        name,
        public_name: "New item",
        color_hex: defaultColorFor(mapDistributors.length),
        show_on_map: true,
        sort_order: mapDistributors.length,
      });
      await refresh();
      setFocusId(id);
    } catch (e) { setErr(e.message); }
  }

  async function handleSettings(patch) {
    try {
      setBusy(true);
      await updateMapSettings(mapId, patch);
      setMap((m) => ({ ...m, ...patch }));
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleLogoUpload(e) {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      setErr("Logo must be a PNG, JPG, or WebP image.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setErr("Logo must be under 2 MB.");
      return;
    }
    setLogoBusy(true);
    setErr(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not signed in.");
      const ext = file.type === "image/png" ? "png" : file.type === "image/webp" ? "webp" : "jpg";
      const path = `${user.id}/${mapId}/logo.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("map-logos")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from("map-logos").getPublicUrl(path);
      const url = `${data.publicUrl}?t=${Date.now()}`;
      const patch = { map_logo_url: url };
      if (!map.map_logo_position) patch.map_logo_position = "top-right";
      await handleSettings(patch);
    } catch (e2) {
      console.error("[MapEditor] logo upload failed:", e2);
      setErr("Could not upload logo.");
    } finally {
      setLogoBusy(false);
    }
  }

  async function handleLogoRemove() {
    try { await handleSettings({ map_logo_url: null }); } catch (e) { setErr(e.message); }
  }

  async function togglePublish() {
    try { await publishMap(mapId, !map.is_published); await refresh(); } catch (e) { setErr(e.message); }
  }

  if (!map) return <p className="muted">Loading map...</p>;

  return (
    <>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <Link to="/distribution-map">&larr; All maps</Link>
        <h1 style={{ margin: 0, fontSize: 20 }}>{map.name}</h1>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="secondary" onClick={() => setExportOpen(true)}>Export</button>
          <button className="btn" onClick={togglePublish}>{map.is_published ? "Unpublish" : "Publish"}</button>
        </div>
      </header>

      {err && <p className="error">{err}</p>}

      <div className="editor-shell">
        <aside className="editor-panel">
          <h2>Map settings</h2>
          <SettingField label="Display title" value={map.map_title ?? ""} onSave={(v) => handleSettings({ map_title: v || null })} />
          <ColorField label="Title color"      value={map.map_title_color}      onSave={(v) => handleSettings({ map_title_color: v })} />
          <ColorField label="Legend text color" value={map.map_legend_text_color} onSave={(v) => handleSettings({ map_legend_text_color: v })} />
          <BgField
            color={map.map_bg_color}
            transparent={!!map.map_bg_transparent}
            onColor={(v) => handleSettings({ map_bg_color: v, map_bg_transparent: false })}
            onTransparent={(t) => handleSettings({ map_bg_transparent: t })}
          />
          <ColorField label="State label"      value={map.map_label_color}      onSave={(v) => handleSettings({ map_label_color: v })} />
          <ColorField label="Unassigned fill"  value={map.map_unassigned_color} onSave={(v) => handleSettings({ map_unassigned_color: v })} />
          <SegmentField
            label="Location of legend"
            value={map.map_legend_position === "right" ? "right" : "bottom"}
            options={[{ value: "bottom", label: "Bottom" }, { value: "right", label: "Right" }]}
            onSave={(v) => handleSettings({ map_legend_position: v })}
          />
          <LogoField
            logoUrl={map.map_logo_url}
            position={map.map_logo_position || "top-right"}
            uploading={logoBusy}
            onUpload={handleLogoUpload}
            onRemove={handleLogoRemove}
            onPosition={(v) => handleSettings({ map_logo_position: v })}
          />
          {busy && <p className="muted">Saving…</p>}

          <h2 style={{ marginTop: 24 }}>{groupLabel}</h2>

          {isManual ? (
            <>
              <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
                Add items and pick a color for each. Select an item's color chip, then click states on the map to paint them.
              </p>
              {distRows.length === 0 && <p className="muted">No legend items yet.</p>}
              {distRows.map((d) => (
                <LegendItemRow
                  key={d.id}
                  d={d}
                  selected={selectedDist === d.name}
                  autoFocus={focusId === d.id}
                  onSelect={() => setSelectedDist(selectedDist === d.name ? null : d.name)}
                  onChange={async (patch) => { await updateDistributor(d.id, patch); await refresh(); }}
                  onDelete={async () => {
                    const ok = await askConfirm({
                      title: "Delete legend item",
                      message: `Delete "${d.public_name || d.name}"? Any states it covers will become unassigned.`,
                      confirmLabel: "Delete",
                      danger: true,
                    });
                    if (!ok) return;
                    if (selectedDist === d.name) setSelectedDist(null);
                    await deleteDistributor(d.id);
                    await refresh();
                  }}
                />
              ))}
              <button className="secondary" style={{ marginTop: 8 }} onClick={handleAddLegendItem}>+ Add legend item</button>
              <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                Click a legend item's color chip to select it, then click a state to assign it. Click an assigned state with nothing selected to clear it.
              </p>
            </>
          ) : (
            <>
              <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
                From <Link to="/system-settings?tab=states">System Settings → States &amp; Distributors</Link>.
                Pick a color for each — the states {isRegion ? "in that region" : "they cover"} are colored automatically.
              </p>
              {isDistributor && (
                <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                  To split a state by county, set it in{" "}
                  <Link to="/system-settings?tab=states">System Settings → States → Counties</Link>.
                  By-county states show a hatch — click one to view its counties.
                </p>
              )}

              {loaded && distRows.length === 0 && (
                <p className="muted">
                  No {groupNoun} yet. Add states &amp; {groupNoun} in{" "}
                  <Link to="/system-settings?tab=states">System Settings</Link>, and they'll appear here.
                </p>
              )}

              {distRows.map((row) => (
                <LegendColorRow
                  key={row.name}
                  row={row}
                  stateCount={stateCounts[row.name] || 0}
                  busy={busy}
                  onColor={(v) => saveDist(row, { color_hex: v })}
                  onName={(v) => saveDist(row, { public_name: v })}
                  onToggle={(v) => saveDist(row, { show_on_map: v })}
                />
              ))}

              {unmatched.length > 0 && (
                <p className="error" style={{ fontSize: 12, marginTop: 10 }}>
                  Couldn't match {unmatched.length} state name{unmatched.length === 1 ? "" : "s"} to the US map:
                  {" "}{[...new Set(unmatched)].join(", ")}. Check the spelling in System Settings (use full names like “California”).
                </p>
              )}
            </>
          )}
        </aside>

        <section className="map-stage" ref={stageRef}>
          {map.map_title && (
            <h2 style={{ textAlign: "center", color: map.map_title_color || "#1f2937" }}>{map.map_title}</h2>
          )}
          <div className={`stage-body ${legendRight ? "legend-right" : ""}`}>
            <div className="stage-map">
              {drill && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <button className="secondary" onClick={exitDrill}>&larr; Back to USA</button>
                  <strong>{drill.name} — by county</strong>
                </div>
              )}
              <div className="map-frame" ref={mapFrameRef}>
                <USMap
                  fills={drill ? drill.fills : fills}
                  unassignedColor={map.map_unassigned_color || "#e5e7eb"}
                  bgColor={map.map_bg_transparent ? null : (map.map_bg_color || "#ffffff")}
                  labelColor={map.map_label_color || "#1f2937"}
                  showLabels={!drill}
                  mixedCodes={drill ? null : mixedCodes}
                  geometry={drill ? drill.geo : null}
                  useCallouts={!drill}
                  viewBox={drill ? drill.viewBox : VIEWBOX}
                  onStateClick={drill ? () => {} : handleNationalClick}
                  onHover={drill ? handleDrillHover : setHovered}
                />
                {map.map_logo_url && !drill && (
                  <img
                    className={`map-logo pos-${map.map_logo_position || "top-right"}`}
                    src={map.map_logo_url}
                    crossOrigin="anonymous"
                    alt=""
                  />
                )}
              </div>
              <p className="hover-info muted" aria-live="polite">
                {drill
                  ? (hoverCounty
                      ? `${hoverCounty.name}${hoverCounty.distributor ? ` — ${hoverCounty.distributor}` : ""}`
                      : (drillBusy ? "Loading…" : " "))
                  : hovered
                    ? `${US_STATE_NAME[hovered]}${hoveredDist ? ` — ${hoveredDist}` : ""}`
                    : (isDistributor && countyModeCodes.size
                        ? "Hatched states are split by county — click one to view its counties."
                        : " ")}
              </p>
            </div>
            <DistributorLegend distributors={distRows} layout={legendRight ? "right" : "bottom"} textColor={legendInk} offsetTop={legendRight ? legendTop : 0} />
          </div>
        </section>
      </div>

      <ExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        getSvg={() => stageRef.current?.querySelector("svg")}
        map={map}
        distributors={distRows}
        companyLogoUrl={companyLogoUrl}
      />
    </>
  );
}

function SettingField({ label, value, onSave }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <label style={{ display: "block", marginTop: 8 }}>
      <span style={{ fontSize: 13 }}>{label}</span>
      <input
        type="text"
        value={v ?? ""}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => v !== value && onSave(v)}
        style={{ width: "100%", padding: 6, border: "1px solid var(--border)", borderRadius: 6, marginTop: 4 }}
      />
    </label>
  );
}

function ColorField({ label, value, onSave }) {
  const [v, setV] = useState(value || "#e5e7eb");
  const timer = useRef();
  useEffect(() => setV(value || "#e5e7eb"), [value]);
  function handle(e) {
    const nv = e.target.value;
    setV(nv);
    if (!HEX_RE.test(nv)) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onSave(nv), 200);
  }
  return (
    <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
      <span style={{ fontSize: 13 }}>{label}</span>
      <input type="color" className="color-picker" value={v} onChange={handle} />
    </label>
  );
}

function SegmentField({ label, value, options, onSave }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
      <span style={{ fontSize: 13 }}>{label}</span>
      <div className="segmented" role="group" aria-label={label}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={value === o.value ? "seg on" : "seg"}
            aria-pressed={value === o.value}
            onClick={() => value !== o.value && onSave(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function LogoField({ logoUrl, position, uploading, onUpload, onRemove, onPosition }) {
  const ref = useRef(null);
  const CORNERS = [
    { value: "top-left", label: "Top L" },
    { value: "top-right", label: "Top R" },
    { value: "bottom-left", label: "Bot L" },
    { value: "bottom-right", label: "Bot R" },
  ];
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 13 }}>Map logo</span>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {logoUrl && (
            <img src={logoUrl} alt="" style={{ height: 24, width: "auto", borderRadius: 4, border: "1px solid var(--border)" }} />
          )}
          <button type="button" className="secondary" disabled={uploading} onClick={() => ref.current?.click()}>
            {uploading ? "…" : logoUrl ? "Replace" : "Upload"}
          </button>
          {logoUrl && <button type="button" className="link" title="Remove logo" onClick={onRemove}>×</button>}
          <input
            ref={ref}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: "none" }}
            onChange={onUpload}
          />
        </div>
      </div>
      {logoUrl && (
        <div style={{ marginTop: 6 }}>
          <SegmentField label="Logo position" value={position} options={CORNERS} onSave={onPosition} />
        </div>
      )}
    </div>
  );
}

function BgField({ color, transparent, onColor, onTransparent }) {
  const [v, setV] = useState(color || "#ffffff");
  const timer = useRef();
  useEffect(() => setV(color || "#ffffff"), [color]);
  function handle(e) {
    const nv = e.target.value;
    setV(nv);
    if (!HEX_RE.test(nv)) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onColor(nv), 200);
  }
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
      <span style={{ fontSize: 13 }}>Background</span>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span className={`color-cross ${transparent ? "crossed" : ""}`}>
          <input
            type="color"
            className="color-picker"
            value={v}
            onChange={handle}
            disabled={transparent}
            title={transparent ? "Transparent — turn off to pick a color" : "Background color"}
            style={{ opacity: transparent ? 0.45 : 1 }}
          />
        </span>
        <button
          type="button"
          className={`chip ${transparent ? "chip-on" : ""}`}
          onClick={() => onTransparent(!transparent)}
          title="Transparent background"
          aria-pressed={transparent}
        >
          <span className="chip-checker" aria-hidden="true" />
          Transparent
        </button>
      </div>
    </div>
  );
}

// A distributor row in the editor: color picker + editable display name +
// state count + show/hide. The distributor itself (and its states) come from
// System Settings — this only edits presentation.
function LegendColorRow({ row, stateCount, busy, onColor, onName, onToggle }) {
  const [label, setLabel] = useState(row.public_name);
  const colorTimer = useRef();
  useEffect(() => setLabel(row.public_name), [row.public_name]);

  function changeColor(e) {
    const v = e.target.value;
    clearTimeout(colorTimer.current);
    colorTimer.current = setTimeout(() => onColor(v), 200);
  }
  function saveLabel() {
    const v = label.trim().slice(0, 200);
    if (v && v !== row.public_name) onName(v);
    else if (!v) setLabel(row.public_name);
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
      <input type="color" className="color-picker" defaultValue={row.color_hex} onChange={changeColor} title="Color" />
      <input
        className="legend-name"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onBlur={saveLabel}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        maxLength={200}
        style={{ flex: "1 1 auto", minWidth: 0 }}
        title="Display name (white-label) — defaults to the distributor name"
      />
      <span className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
        {stateCount} {stateCount === 1 ? "state" : "states"}
      </span>
      <label title="Show on map" style={{ display: "inline-flex", alignItems: "center" }}>
        <input type="checkbox" checked={row.show_on_map} disabled={busy} onChange={(e) => onToggle(e.target.checked)} />
      </label>
    </div>
  );
}

// A manual ('other' map) legend item: a color chip you select to paint states,
// an editable name, a color picker, and delete.
function LegendItemRow({ d, selected, autoFocus, onSelect, onChange, onDelete }) {
  const [label, setLabel] = useState(d.public_name || d.name);
  const colorTimer = useRef();
  const inputRef = useRef(null);
  useEffect(() => setLabel(d.public_name || d.name), [d.public_name, d.name]);
  useEffect(() => {
    if (autoFocus && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [autoFocus]);

  function saveLabel() {
    const v = label.trim().slice(0, 200);
    if (v && v !== (d.public_name || d.name)) onChange({ public_name: v });
    else if (!v) setLabel(d.public_name || d.name);
  }
  function changeColor(e) {
    const v = e.target.value;
    clearTimeout(colorTimer.current);
    colorTimer.current = setTimeout(() => onChange({ color_hex: v }), 200);
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        title={selected ? "Selected — click a state on the map to assign it" : "Select, then click states to paint them"}
        style={{
          width: 28, height: 28, padding: 0, flex: "0 0 auto", borderRadius: 7,
          background: d.color_hex || "#9ca3af",
          border: selected ? "2px solid var(--primary)" : "1px solid var(--border)",
          boxShadow: selected ? "0 0 0 3px var(--primary-soft)" : "none",
          cursor: "pointer",
        }}
      />
      <input
        ref={inputRef}
        className="legend-name"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onBlur={saveLabel}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        maxLength={200}
        placeholder="Name"
        style={{ flex: "1 1 auto", minWidth: 0 }}
      />
      <input type="color" className="color-picker" defaultValue={d.color_hex || "#9ca3af"} onChange={changeColor} title="Color" />
      <button className="link" onClick={onDelete} title="Delete" style={{ color: "var(--error)", fontSize: 18, padding: "0 4px" }}>×</button>
    </div>
  );
}
